import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { FileText, Clock, AlertTriangle, ShoppingCart, Plus, Activity, Wallet, Send, Sparkles } from 'lucide-react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';

const inr = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;
const compact = (n) => (n >= 1e7 ? `₹${(n / 1e7).toFixed(1)}Cr` : n >= 1e5 ? `₹${(n / 1e5).toFixed(1)}L` : n >= 1e3 ? `₹${(n / 1e3).toFixed(1)}K` : inr(n));
const greeting = () => { const h = new Date().getHours(); return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening'; };
const timeAgo = (iso) => {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.round(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};
const Panel = ({ title, icon: Icon, right, children, testid, className = '' }) => (
  <div className={`lg-glass rounded-2xl border p-5 ${className}`} data-testid={testid}>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-sm font-semibold text-stone-800 flex items-center gap-2">{Icon && <Icon size={16} className="text-stone-500" />}{title}</h3>
      {right}
    </div>
    {children}
  </div>
);

const Greeting = ({ name, summary }) => (
  <div className="lg-glass rounded-3xl border p-6 md:p-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4" data-testid="vendor-greeting">
    <div>
      <div className="text-xs uppercase tracking-[0.2em] text-stone-500">{new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</div>
      <h1 className="text-3xl md:text-4xl font-semibold text-stone-900 mt-1" data-testid="vendor-greeting-title">{greeting()}, {name}</h1>
      <p className="mt-2 text-sm text-stone-600 flex items-center gap-2" data-testid="vendor-greeting-summary"><Sparkles size={14} className="text-amber-500" />{summary}</p>
    </div>
    <Link to="/vendor/rates"><Button data-testid="vendor-submit-rates-btn"><Plus size={16} /> Submit New Rates</Button></Link>
  </div>
);

const STAT_DEFS = [
  { key: 'new_boqs', label: 'New BOQs Received', icon: FileText, tone: (v) => (v ? 'bg-sky-500' : 'bg-stone-300') },
  { key: 'quotes_pending', label: 'Quotes Pending', icon: Clock, tone: (v) => (v > 3 ? 'bg-amber-500' : v ? 'bg-amber-400' : 'bg-stone-300') },
  { key: 'urgent', label: 'Urgent Items', icon: AlertTriangle, tone: (v) => (v ? 'bg-red-500 animate-pulse' : 'bg-emerald-500') },
  { key: 'purchase_orders', label: 'Purchase Orders Received', icon: ShoppingCart, tone: (v) => (v ? 'bg-emerald-500' : 'bg-stone-300') },
];
const StatCards = ({ stats }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="vendor-stat-cards">
    {STAT_DEFS.map(({ key, label, icon: Icon, tone }) => (
      <div key={key} className="lg-glass rounded-2xl border p-5 hover:-translate-y-0.5" data-testid={`vendor-stat-${key}`}>
        <div className="flex items-center justify-between">
          <Icon size={18} className="text-stone-500" />
          <span className={`w-2.5 h-2.5 rounded-full ${tone(stats[key] || 0)}`} data-testid={`vendor-stat-${key}-indicator`} />
        </div>
        <div className="text-3xl font-semibold text-stone-900 mt-3" data-testid={`vendor-stat-${key}-value`}>{stats[key] || 0}</div>
        <div className="text-xs text-stone-500 mt-1">{label}</div>
      </div>
    ))}
  </div>
);

const Funnel = ({ funnel }) => (
  <Panel title="Performance Funnel" icon={Activity} testid="vendor-funnel">
    <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
      {funnel.map((s, i) => (
        <div key={s.key} className="relative rounded-xl bg-white/50 border border-white/60 px-3 py-3" data-testid={`vendor-funnel-${s.key}`}>
          <div className="text-[10px] uppercase tracking-wider text-stone-500">{s.label}</div>
          {s.key === 'win_rate' ? (
            <div className="text-2xl font-semibold text-emerald-700 mt-1">{s.pct}%</div>
          ) : (
            <>
              <div className="text-2xl font-semibold text-stone-900 mt-1">{s.count}</div>
              <div className="text-xs text-stone-600 font-mono">{compact(s.amount)}</div>
            </>
          )}
          {i < funnel.length - 1 && <span className="hidden md:block absolute -right-2 top-1/2 -translate-y-1/2 text-stone-300 z-10">›</span>}
        </div>
      ))}
    </div>
  </Panel>
);

const fyStart = () => { const d = new Date(); const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1; return new Date(y, 3, 1); };
const RevenueTrend = ({ series }) => {
  const [range, setRange] = useState('all');
  const data = useMemo(() => {
    const from = range === '30d' ? new Date(Date.now() - 30 * 864e5) : range === 'fy' ? fyStart() : null;
    const buckets = {};
    series.filter((p) => p.date && (!from || new Date(p.date) >= from)).forEach((p) => {
      const k = range === '30d' ? p.date : p.date.slice(0, 7);
      buckets[k] = (buckets[k] || 0) + p.amount;
    });
    return Object.entries(buckets).sort().map(([k, v]) => ({ period: k, revenue: Math.round(v) }));
  }, [series, range]);
  const toggle = (
    <div className="inline-flex rounded-full lg-btn-glass p-0.5 text-[11px]" data-testid="vendor-revenue-range">
      {[['30d', '30D'], ['fy', 'FY'], ['all', 'All']].map(([k, l]) => (
        <button key={k} onClick={() => setRange(k)} className={`px-3 py-1 rounded-full transition-colors ${range === k ? 'bg-stone-900 text-white' : 'text-stone-600'}`} data-testid={`vendor-revenue-range-${k}`}>{l}</button>
      ))}
    </div>
  );
  return (
    <Panel title="Revenue Trend" icon={Wallet} right={toggle} testid="vendor-revenue-trend">
      {data.length ? (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={data}><CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
            <XAxis dataKey="period" tick={{ fontSize: 10 }} /><YAxis tickFormatter={compact} tick={{ fontSize: 10 }} width={56} />
            <Tooltip formatter={(v) => inr(v)} /><Bar dataKey="revenue" fill="var(--brand-button, #0f766e)" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      ) : <div className="h-[220px] flex items-center justify-center text-sm text-stone-400" data-testid="vendor-revenue-empty">No payments in this range yet</div>}
    </Panel>
  );
};

const ProjectPerformance = ({ rows }) => (
  <Panel title="Project Performance · BOQ value" icon={FileText} testid="vendor-project-performance">
    {rows.length ? (
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={rows} layout="vertical" margin={{ left: 8 }}><CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" horizontal={false} />
          <XAxis type="number" tickFormatter={compact} tick={{ fontSize: 10 }} /><YAxis type="category" dataKey="project" width={130} tick={{ fontSize: 10 }} />
          <Tooltip formatter={(v) => inr(v)} /><Bar dataKey="value" fill="var(--brand-primary-hex, #475569)" radius={[0, 6, 6, 0]} />
        </BarChart>
      </ResponsiveContainer>
    ) : <div className="h-[220px] flex items-center justify-center text-sm text-stone-400">No linked projects yet</div>}
  </Panel>
);

const Efficiency = ({ e }) => {
  const cells = [
    ['Response Rate', `${e.response_rate}%`, 'eff-rate'], ['Responded', e.responded, 'eff-responded'], ['Missed', e.missed, 'eff-missed'],
    ['Avg. Response', e.avg_response_hours == null ? '—' : `${e.avg_response_hours}h`, 'eff-avg'], ['Within 24h', e.within_24h, 'eff-24h'],
  ];
  return (
    <Panel title="Response Efficiency" icon={Send} testid="vendor-efficiency">
      <div className="h-2 rounded-full bg-stone-200/70 overflow-hidden mb-4"><div className="h-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${e.response_rate}%` }} /></div>
      <div className="grid grid-cols-5 gap-2">
        {cells.map(([l, v, t]) => (
          <div key={t} className="text-center" data-testid={`vendor-${t}`}><div className="text-xl font-semibold text-stone-900">{v}</div><div className="text-[10px] text-stone-500 leading-tight">{l}</div></div>
        ))}
      </div>
    </Panel>
  );
};

const DOT = { boq_shared: 'bg-sky-500', payment: 'bg-emerald-500', quote: 'bg-amber-500', po: 'bg-violet-500' };
const ActivityFeed = ({ events }) => (
  <Panel title="Recent Activity" icon={Activity} testid="vendor-activity">
    {events.length ? (
      <ul className="space-y-3">
        {events.map((ev, i) => (
          <li key={i} className="flex items-start gap-3" data-testid={`vendor-activity-${i}`}>
            <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${DOT[ev.type] || 'bg-stone-400'}`} />
            <div className="min-w-0 flex-1"><div className="text-sm text-stone-800 truncate">{ev.title}</div><div className="text-[11px] text-stone-500">{ev.project}</div></div>
            <span className="text-[11px] text-stone-400 whitespace-nowrap">{timeAgo(ev.at)}</span>
          </li>
        ))}
      </ul>
    ) : <div className="text-sm text-stone-400">No activity yet</div>}
  </Panel>
);

export const VendorInsights = ({ fallbackName }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    axios.get(`${API}/vendor/dashboard/insights`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then((r) => setD(r.data)).catch(() => setD(false));
  }, []);
  if (d === null) return <div className="lg-glass rounded-3xl border h-40 animate-pulse" data-testid="vendor-insights-loading" />;
  if (d === false) return <Greeting name={fallbackName} summary="Welcome back." />;
  return (
    <div className="space-y-5" data-testid="vendor-insights">
      <Greeting name={d.vendor_name || fallbackName} summary={d.summary} />
      <StatCards stats={d.stats} />
      <Funnel funnel={d.funnel} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5"><RevenueTrend series={d.revenue_series} /><ProjectPerformance rows={d.project_performance} /></div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5"><Efficiency e={d.efficiency} /><ActivityFeed events={d.activity} /></div>
    </div>
  );
};
